import { App } from 'antd';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import EnterFilePasswordForm from '../../components/Forms/EnterFilePasswordForm';
import { useAppStore } from '../../store/store';

/**
 * Custom hook that manages the automatic file password authentication flow.
 * When the application has a pending file password request (such as when a protected file/folder is accessed),
 * this hook will present a modal dialog to prompt the user for the file password.
 * On success, the pending request is cleared and the app proceeds to re-fetch the necessary data.
 * On cancel: if the pending was set by getFiles 403 (user landed on protected folder URL), redirect to section root; otherwise just close the modal.
 *
 * This hook is meant to be used at the app's root or route level so it can operate globally,
 * and it should be rendered unconditionally so password requests can always interrupt the normal UI flow when needed.
 */
export function useFilePasswordAccess() {
  // Get the relevant state and actions from the global app store.
  const {
    pendingFilePasswordRequest,
    clearPendingFilePasswordRequest,
    setFileIdToNavigateAfterPassword,
    setPendingFilePasswordFromMoveForm,
    setPendingFilePasswordFromUpload,
    setPendingFilePasswordCancelledByUser,
  } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { modal } = App.useApp();
  const { t } = useTranslation();
  const modalInstanceRef = useRef<ReturnType<typeof modal.confirm> | null>(null);

  // Monitor changes to pendingFilePasswordRequest for debugging purposes.
  useEffect(() => {
    if (pendingFilePasswordRequest) {
      // Show the modal with the password form
      const modalInstance = modal.confirm({
        title: t('modal.enterFolderPasswordForm.title') || 'Protected Folder',
        content: (
          <EnterFilePasswordForm
            content={
              <p className="mb-4 text-base">
                <Trans>
                  {t('modal.enterFolderPasswordForm.description', {
                    folderName: pendingFilePasswordRequest.fileName,
                  }) || `The folder "${pendingFilePasswordRequest.fileName}" requires a password.`}
                </Trans>
              </p>
            }
            fileId={pendingFilePasswordRequest.requestedFileId ?? pendingFilePasswordRequest.fileId}
            isOwner={false}
            onSubmit={async () => {
              try {
                clearPendingFilePasswordRequest();
                modalInstance.destroy();
              } catch (error) {
                // Error already handled in form
              }
            }}
            onCancel={() => {
              const state = useAppStore.getState();
              const fromMoveForm = state.pendingFilePasswordFromMoveForm;
              const fromUpload = state.pendingFilePasswordFromUpload;
              const fromViewer = state.pendingFilePasswordSource === 'viewer';
              setPendingFilePasswordCancelledByUser(true);
              clearPendingFilePasswordRequest();
              setFileIdToNavigateAfterPassword(null);
              if (fromMoveForm || fromUpload) {
                setPendingFilePasswordFromMoveForm(false);
                setPendingFilePasswordFromUpload(false);
              } else if (state.fileIdToNavigateAfterPassword == null) {
                const section = fromViewer
                  ? 'personal'
                  : location.pathname.split('/')[2] || 'personal';
                navigate(`/files/${section}`);
              }
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
        closable: false,
        mask: { closable: false },
        width: 500,
      });

      modalInstanceRef.current = modalInstance;
    } else {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.destroy();
        modalInstanceRef.current = null;
      }
    }

    return () => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.destroy();
        modalInstanceRef.current = null;
      }
    };
  }, [
    pendingFilePasswordRequest,
    clearPendingFilePasswordRequest,
    setFileIdToNavigateAfterPassword,
    setPendingFilePasswordFromMoveForm,
    setPendingFilePasswordFromUpload,
    setPendingFilePasswordCancelledByUser,
    navigate,
    location.pathname,
    modal,
    t,
  ]);

  return null;
}
